/**
 * MUI TabNavigation Component
 * 
 * This component provides tab navigation functionality for equipment list views.
 * Used in navigation sections for view selection (Summary, Microphone locations, etc.)
 */

import React from 'react';
import {
  Box,
  Tabs,
  Tab,
  styled,
  useTheme
} from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentValue2 } from 'reduxModules/selectors/containerSelectors';
import { uiReducers } from 'reduxModules/ducks/ui';
import type { RootState } from 'types/redux/state';

interface TabNavigationProps {
  name: string;
  items: Array<{ value: string; label: string } | string>;
  dispatcher: string;
  isNav?: boolean;
  className?: string;
  [key: string]: any;
}

/**
 * TabNavigation component for equipment list view selection
 * 
 * @param props - Component props
 * @returns TabNavigation component
 */
const TabNavigation: React.FC<TabNavigationProps> = (props) => {
  const {
    name,
    items = [],
    dispatcher,
    className = '',
    isNav = false,
    ...otherProps
  } = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  
  const currentValue = useSelector((state: RootState) => 
    name === 'showTable' ? state.ui.showTable : getCurrentValue2(state, name)
  );

  const isEquipmentListNav = name === 'showTable' && isNav && dispatcher === 'setShowTable';
  const defaultValue = isEquipmentListNav ? 'summary' : '';
  const effectiveValue = currentValue || defaultValue;

  React.useEffect(() => {
    if (isEquipmentListNav && !currentValue) {
      dispatch(uiReducers.setShowTable('summary'));
    }
  }, [isEquipmentListNav, currentValue, dispatch]);

  const handleChange = (event: React.SyntheticEvent, value: string) => {
    if (dispatcher === 'setShowTable') {
      dispatch(uiReducers.setShowTable(value));
    } else if (dispatcher) {
      dispatch({ type: dispatcher, payload: value });
    }
  };

  const tabItems = items.map((item) => {
    const value = typeof item === 'object' ? item.value : item;
    const label = typeof item === 'object' ? item.label : item;
    return { value, label };
  });

  return (
    <StyledBox className={className} isNav={isNav}>
      <StyledTabs
        value={effectiveValue}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        isNav={isNav}
      >
        {tabItems.map((item, index) => (
          <StyledTab
            key={`${name}-tab-${index}`}
            value={item.value}
            label={item.label}
            isNav={isNav}
          />
        ))}
      </StyledTabs>
    </StyledBox>
  );
};

const StyledBox = styled(Box)<{ isNav?: boolean }>(({ theme, isNav }) => ({
  width: '100%',
  marginBottom: isNav ? '20px' : '12px',
  backgroundColor: 'transparent',
}));

const StyledTabs = styled(Tabs)<{ isNav?: boolean }>(({ theme, isNav }) => ({
  minHeight: '40px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    backgroundColor: '#007bff',
    height: '3px',
    borderRadius: '3px 3px 0 0',
  },
  '& .MuiTabs-flexContainer': {
    gap: '0px',
  },
  '& .MuiTabs-scrollButtons': {
    '&.Mui-disabled': {
      opacity: 0.3,
    },
  },
}));

const StyledTab = styled(Tab)<{ isNav?: boolean }>(({ theme, isNav }) => ({
  textTransform: 'none',
  minWidth: 'auto',
  minHeight: '40px',
  padding: '8px 20px',
  marginRight: '0px',
  fontSize: '15px',
  fontWeight: 400,
  fontFamily: '"Segoe UI", system-ui, -apple-system, sans-serif',
  color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
  backgroundColor: 'transparent',
  borderBottom: '3px solid transparent',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.04)',
    color: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.9)' 
      : 'rgba(0, 0, 0, 0.87)',
  },
  '&.Mui-selected': {
    color: '#007bff',
    fontWeight: 500,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? 'rgba(0, 123, 255, 0.08)' 
        : 'rgba(0, 123, 255, 0.04)',
    },
  },
  '&.Mui-focusVisible': {
    backgroundColor: theme.palette.action.focus,
  },
}));

export default TabNavigation;